import React, { useCallback, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/Button/Button";
import { useAppDispatch } from "@/app/hooks";
import DataTable from "@/components/Datatable/Datatable";
import { useEventSessions } from "@/modules/eventRegistration/features/EventSessionManagement/hooks/useEventSessions";
import EventSessionFilters from "@/modules/eventRegistration/features/EventSessionManagement/components/EventSessionFilters/EventSessionFilters";
import DeleteEventSessionModal from "@/modules/eventRegistration/features/EventSessionManagement/components/DeleteEventSessionModal/DeleteEventSessionModal";
import CancelEventSessionModal from "@/modules/eventRegistration/features/EventSessionManagement/components/CancelEventSessionModal/CancelEventSessionModal";
import CreateEventSessionDrawer from "@/modules/eventRegistration/features/EventSessionManagement/components/CreateEventSessionDrawer/CreateEventSessionDrawer";
import EditEventSessionDrawer from "@/modules/eventRegistration/features/EventSessionManagement/components/EditEventSessionDrawer/EditEventSessionDrawer";
import { setPublishedEventSessionAction } from "@/modules/eventRegistration/features/EventSessionManagement/slices/eventSessionListSlice";
import { createEventSessionColumns } from "@/modules/eventRegistration/features/EventSessionManagement/components/EventSessionColumns/EventSessionColumns";
import { SessionSummary } from "@/modules/eventRegistration/features/EventSessionManagement/api/fetchEventSessions/types";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";

function EventSessionList() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { uuid: eventUuid } = useParams();

  const {
    sessions,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handleFilterChange,
    handlePaginationChange,
    handleSortingChange,
    refetchSessions,
    eventName,
  } = useEventSessions(eventUuid);

  const [isCreateDrawerOpen, setCreateDrawerOpen] = useState(false);
  const openCreateDrawer = useCallback(() => {
    setCreateDrawerOpen(true);
  }, []);
  const closeCreateDrawer = useCallback(() => {
    setCreateDrawerOpen(false);
  }, []);

  const [isEditDrawerOpen, setEditDrawerOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState<string | null>(null);
  const openEditDrawer = useCallback((uuid: string) => {
    setSessionToEdit(uuid);
    setEditDrawerOpen(true);
  }, []);
  const closeEditDrawer = useCallback(() => {
    setEditDrawerOpen(false);
    setSessionToEdit(null);
  }, []);

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);

  const handleDeleteSession = useCallback((uuid: string) => {
    setSessionToDelete(uuid);
    setDeleteModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSessionToDelete(null);
  }, []);

  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const [sessionToCancel, setSessionToCancel] = useState<string | null>(null);

  const handleCancelSession = useCallback((uuid: string) => {
    setSessionToCancel(uuid);
    setCancelModalOpen(true);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setCancelModalOpen(false);
    setSessionToCancel(null);
  }, []);

  const handleFiltersChange = useCallback(
    (filterKey: string, value: string | string[]) => {
      handleFilterChange(filterKey, value);
      handlePaginationChange({
        pageIndex: 0,
        pageSize: pagination.pageSize,
      });
    },
    [handleFilterChange, handlePaginationChange, pagination.pageSize],
  );

  const handleTogglePublishedSession = useCallback(
    (sessionUuid: string, newVal: boolean) => {
      dispatch(setPublishedEventSessionAction(sessionUuid, newVal));
    },
    [dispatch],
  );

  const handleRowClick = useCallback(
    (session: SessionSummary) => {
      navigate(`/admin/events/${eventUuid}/sessions/${session.uuid}/waitlist`);
    },
    [navigate, eventUuid],
  );

  const columns = useMemo(
    () =>
      createEventSessionColumns({
        onDeleteSession: handleDeleteSession,
        onCancelSession: handleCancelSession,
        onEditSession: openEditDrawer,
        onTogglePublishedSession: handleTogglePublishedSession,
      }),
    [handleDeleteSession, handleCancelSession, openEditDrawer, handleTogglePublishedSession],
  );

  return (
    <>
      <MainPageWrapper
        error={error}
        hideHeader
      >
        <div className="flex items-center justify-between -mt-1 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              className="p-1 -ml-1 rounded text-gray-600 hover:text-gray-900 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
              onClick={() => navigate("/admin/events")}
              type="button"
            >
              <ChevronLeftIcon className="h-5 w-5 stroke-current stroke-1" />
            </button>
            <h1 className="text-[15px] font-medium text-gray-900 dark:text-slate-100">
              Event Sessions
            </h1>
          </div>
          <div className="flex-shrink-0 ml-4 [&_button]:h-7 [&_button]:px-2.5 [&_button]:text-xs">
            <Button onClick={openCreateDrawer}>Create Session</Button>
          </div>
        </div>

        <EventSessionFilters
          filters={filters}
          onFilterChange={handleFiltersChange}
        />

        <DataTable
          columns={columns}
          data={sessions}
          error={error}
          loading={loading}
          noDataMessage="No sessions found"
          onPageChange={(newPageIndex, newPageSize) =>
            handlePaginationChange({
              pageIndex: newPageIndex,
              pageSize: newPageSize,
            })
          }
          onRowClick={handleRowClick}
          onSortingChange={handleSortingChange}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowKeyExtractor={(item) => item.uuid}
          sorting={sorting}
          totalCount={totalCount}
        />
      </MainPageWrapper>

      {eventUuid && (
        <CreateEventSessionDrawer
          eventUuid={eventUuid}
          isOpen={isCreateDrawerOpen}
          onClose={closeCreateDrawer}
          onSuccess={() => {
            closeCreateDrawer();
            refetchSessions();
          }}
        />
      )}

      {eventUuid && (
        <EditEventSessionDrawer
          eventUuid={eventUuid}
          isOpen={isEditDrawerOpen}
          onClose={closeEditDrawer}
          onSuccess={() => {
            closeEditDrawer();
            refetchSessions();
          }}
          sessionUuid={sessionToEdit}
        />
      )}

      <DeleteEventSessionModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        onSuccess={() => {
          handleCloseModal();
          refetchSessions();
        }}
        sessionUuid={sessionToDelete}
      />

      <CancelEventSessionModal
        isOpen={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onSuccess={() => {
          handleCloseCancelModal();
          refetchSessions();
        }}
        sessionUuid={sessionToCancel}
      />
    </>
  );
}

export default EventSessionList;
